(function () {
  'use strict';

  const SCROLL_DURATION = 120;

  function getImageSwatchesContainer() {
    return document.querySelector(
      '.product-form__input--image-swatches .image-swatches-scroll'
    );
  }

  function getSelectedSwatch(container) {
    if (!container) return null;

    const checkedInput = container.querySelector(
      'input[type="radio"]:checked'
    );

    if (!checkedInput) return null;

    return checkedInput.nextElementSibling;
  }

  /*
   * Calculate the scroll position from the actual
   * visual center of the container and the swatch.
   *
   * This is more accurate than using offsetLeft because
   * the container also has padding, gap and borders.
   */
  function getTargetScrollLeft(container, element) {
    if (!container || !element) return 0;

    const containerRect =
      container.getBoundingClientRect();

    const elementRect =
      element.getBoundingClientRect();

    const containerCenter =
      containerRect.left +
      containerRect.width / 2;

    const elementCenter =
      elementRect.left +
      elementRect.width / 2;

    let target =
      container.scrollLeft +
      (elementCenter - containerCenter);

    const maxScroll =
      container.scrollWidth -
      container.clientWidth;

    target = Math.max(
      0,
      Math.min(target, maxScroll)
    );

    return target;
  }

  /*
   * Immediately move the scroll position.
   * Used after page refresh and after Dawn replaces
   * the variant picker DOM.
   */
  function setScrollPosition(container, element) {
    if (!container || !element) return;

    const target =
      getTargetScrollLeft(
        container,
        element
      );

    container.scrollLeft = target;
  }

  /*
   * Fast smooth scrolling for user clicks.
   */
  function animateScroll(
    container,
    target,
    duration
  ) {
    if (!container) return;

    const start =
      container.scrollLeft;

    const distance =
      target - start;

    if (Math.abs(distance) < 1) {
      container.scrollLeft = target;
      return;
    }

    const startTime =
      performance.now();

    function step(currentTime) {
      const elapsed =
        currentTime - startTime;

      const progress =
        Math.min(
          elapsed / duration,
          1
        );

      /*
       * Ease-out cubic.
       * Fast at the beginning and quickly settles.
       */
      const eased =
        1 -
        Math.pow(
          1 - progress,
          3
        );

      container.scrollLeft =
        start +
        distance * eased;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }

  /*
   * Center the currently selected Color.
   *
   * Two animation frames are used so the browser has
   * enough time to finish the new DOM/layout calculation
   * after Dawn updates the variant.
   */
  function centerSelectedSwatch(
    animate
  ) {
    const container =
      getImageSwatchesContainer();

    if (!container) return;

    const selected =
      getSelectedSwatch(
        container
      );

    if (!selected) return;

    requestAnimationFrame(
      function () {
        requestAnimationFrame(
          function () {
            const target =
              getTargetScrollLeft(
                container,
                selected
              );

            if (!animate) {
              container.scrollLeft =
                target;
            } else {
              animateScroll(
                container,
                target,
                SCROLL_DURATION
              );
            }
          }
        );
      }
    );
  }

  /*
   * Initial positioning.
   */
  function centerSelectedImmediately() {
    const container =
      getImageSwatchesContainer();

    if (!container) return;

    const selected =
      getSelectedSwatch(
        container
      );

    if (!selected) return;

    /*
     * Wait for image/layout calculation.
     */
    requestAnimationFrame(
      function () {
        requestAnimationFrame(
          function () {
            setScrollPosition(
              container,
              selected
            );
          }
        );
      }
    );
  }

  /*
   * Check whether the changed input belongs
   * to our Image Swatches Color picker.
   */
  function isColorSwatchInput(
    target
  ) {
    if (!target) return false;

    if (
      target.tagName !== 'INPUT' ||
      target.type !== 'radio'
    ) {
      return false;
    }

    const fieldset =
      target.closest(
        '.product-form__input--image-swatches'
      );

    return !!fieldset;
  }

  /*
   * Handle user clicking a Color.
   */
  function handleSwatchClick(
    event
  ) {
    const input =
      event.target.closest(
        '.product-form__input--image-swatches input[type="radio"]'
      );

    if (!input) return;

    const container =
      input
        .closest(
          '.product-form__input--image-swatches'
        )
        ?.querySelector(
          '.image-swatches-scroll'
        );

    if (!container) return;

    const label =
      input.nextElementSibling;

    if (!label) return;

    /*
     * Let the radio button become checked first,
     * then calculate its actual position.
     */
    requestAnimationFrame(
      function () {
        requestAnimationFrame(
          function () {
            const target =
              getTargetScrollLeft(
                container,
                label
              );

            animateScroll(
              container,
              target,
              SCROLL_DURATION
            );
          }
        );
      }
    );
  }

  /*
   * Capture the Color change before Dawn's
   * variant update process changes the DOM.
   */
  function handleVariantChange(
    event
  ) {
    if (!event.target) return;

    if (
      !isColorSwatchInput(
        event.target
      )
    ) {
      return;
    }

    handleSwatchClick(event);
  }

  /*
   * Dawn may replace the entire variant picker
   * after a variant selection.
   *
   * Detect that replacement and immediately
   * restore the selected Color's scroll position.
   */
  function observeVariantReplacement() {
    let lastVariantSelects = null;

    const observer =
      new MutationObserver(
        function () {
          const currentVariantSelects =
            document.querySelector(
              'variant-selects'
            );

          if (
            currentVariantSelects &&
            currentVariantSelects !==
              lastVariantSelects
          ) {
            lastVariantSelects =
              currentVariantSelects;

            centerSelectedImmediately();
          }
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  /*
   * Initialize.
   */
  function initialize() {
    /*
     * Center selected Color on initial page load.
     */
    centerSelectedImmediately();

    /*
     * Listen for Color changes.
     */
    document.addEventListener(
      'change',
      handleVariantChange,
      true
    );

    /*
     * Handle browser back/forward cache.
     */
    window.addEventListener(
      'pageshow',
      function () {
        centerSelectedImmediately();
      }
    );

    /*
     * Handle final page layout.
     */
    window.addEventListener(
      'load',
      function () {
        centerSelectedImmediately();
      }
    );

    /*
     * Watch for Dawn's variant picker replacement.
     */
    observeVariantReplacement();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }
})();